import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Eye, Zap } from "lucide-react";
import { fetchVehiculos } from "@/lib/api";
import VehiculoModal from "@/components/VehiculoModal";

export const money = (n) => "$" + Number(n).toLocaleString("es-MX");

function Badge({ children }) {
  return (
    <span className="text-[11px] font-semibold px-2 py-1 rounded-md"
      style={{ background: "rgba(232,93,4,0.15)", color: "var(--af-orange-bright)", border: "1px solid rgba(232,93,4,0.35)" }}>
      {children}
    </span>
  );
}

function Card({ v, onDetails, onPrecal }) {
  return (
    <div data-testid={`veh-card-${v.id}`} className="af-card overflow-hidden shrink-0 flex flex-col"
      style={{ width: 320 }}>
      <div className="relative" style={{ background: "#0a0a0a" }}>
        <img src={v.imagen} alt={`${v.marca} ${v.modelo}`} className="w-full object-cover" style={{ height: 190 }} loading="lazy" />
        <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
          {v.badges?.map((b) => <Badge key={b}>{b}</Badge>)}
        </div>
        {Number(v.ahorro_mxn) > 0 && (
          <div className="absolute top-3 right-3 text-xs font-bold px-2.5 py-1 rounded-md"
            style={{ background: "var(--af-orange-deep)", color: "#fff" }}>
            -${Number(v.ahorro_mxn).toLocaleString("es-MX", { maximumFractionDigits: 2 })}
          </div>
        )}
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="text-white/50 text-xs uppercase tracking-wide">{v.marca} · {v.anio}</div>
        <div className="text-white font-bold text-lg leading-tight">{v.modelo}</div>
        <div className="text-white/60 text-sm">{v.version}</div>

        <div className="mt-4 flex items-end gap-2">
          <div className="text-af-orange-core font-bold text-xl">{money(v.precio_autofinanza)}</div>
          <div className="text-white/40 text-sm line-through mb-0.5">{money(v.precio_lista)}</div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
          <div className="rounded-lg p-2" style={{ background: "#111" }}>
            <div className="text-white/50">Mensualidad desde</div>
            <div className="text-white font-semibold">{money(v.mensualidad_desde)}</div>
          </div>
          <div className="rounded-lg p-2" style={{ background: "#111" }}>
            <div className="text-white/50">Enganche estim.</div>
            <div className="text-white font-semibold">{money(v.enganche_estimado)}</div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {v.modalidades_disponibles?.map((m) => (
            <span key={m} className="text-[11px] text-white/70 px-2 py-0.5 rounded border border-af-border-dark">{m}</span>
          ))}
        </div>

        <div className="flex gap-2 mt-4 pt-1">
          <button data-testid={`veh-detalles-${v.id}`} onClick={() => onDetails(v)}
            className="af-btn-ghost flex-1" style={{ padding: "0.6rem" }}>
            <Eye size={16} /> Ver detalles
          </button>
        </div>
        <button data-testid={`veh-precal-${v.id}`} onClick={() => onPrecal(v)}
          className="af-btn-primary w-full mt-2" style={{ padding: "0.7rem", fontSize: "0.92rem" }}>
          <Zap size={16} /> Iniciar precalificación
        </button>
      </div>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="af-card overflow-hidden shrink-0 animate-pulse" style={{ width: 320 }}>
      <div style={{ height: 190, background: "#151515" }} />
      <div className="p-5 space-y-3">
        <div className="h-3 w-24 rounded bg-[#1d1d1d]" />
        <div className="h-5 w-40 rounded bg-[#1d1d1d]" />
        <div className="h-6 w-32 rounded bg-[#1d1d1d]" />
        <div className="h-16 rounded bg-[#151515]" />
        <div className="h-9 rounded bg-[#1d1d1d]" />
      </div>
    </div>
  );
}

export default function Carrusel() {
  const navigate = useNavigate();
  const scroller = useRef(null);
  const [vehiculos, setVehiculos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetchVehiculos()
      .then((v) => setVehiculos(v))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const scrollBy = (dir) => {
    if (scroller.current) scroller.current.scrollBy({ left: dir * 350, behavior: "smooth" });
  };

  const onPrecal = (v) => navigate("/registro", { state: { vehiculo: `${v.marca} ${v.modelo} ${v.version}` } });

  return (
    <section id="ofertas" className="py-16 md:py-20" style={{ background: "var(--af-black)" }}>
      <div className="af-container">
        <div className="flex items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-af-orange-core font-display uppercase tracking-widest text-sm mb-2">Ofertas destacadas</div>
            <h2 className="h-impact text-white text-3xl md:text-4xl">Vehículos que podrían ser para ti</h2>
          </div>
          <div className="hidden md:flex gap-2">
            <button data-testid="carrusel-prev" onClick={() => scrollBy(-1)} className="af-btn-ghost" style={{ padding: "0.6rem" }}><ChevronLeft size={20} /></button>
            <button data-testid="carrusel-next" onClick={() => scrollBy(1)} className="af-btn-ghost" style={{ padding: "0.6rem" }}><ChevronRight size={20} /></button>
          </div>
        </div>

        {error ? (
          <div data-testid="carrusel-error" className="af-card p-10 text-center text-white/70">
            No pudimos cargar los vehículos en este momento. Vuelve a intentarlo en unos minutos.
          </div>
        ) : (
          <div ref={scroller} data-testid="carrusel-scroller"
            className="flex gap-5 overflow-x-auto pb-4" style={{ scrollbarWidth: "thin" }}>
            {loading
              ? Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} />)
              : vehiculos.length === 0
                ? <div data-testid="carrusel-empty" className="af-card p-10 text-center text-white/70 w-full">Aún no hay vehículos disponibles.</div>
                : vehiculos.map((v) => <Card key={v.id} v={v} onDetails={setSelected} onPrecal={onPrecal} />)}
          </div>
        )}

        <p className="text-white/45 text-xs mt-4">La aprobación y condiciones finales dependen de la institución correspondiente.</p>
      </div>

      <VehiculoModal vehiculo={selected} onClose={() => setSelected(null)} onPrecal={onPrecal} />
    </section>
  );
}
